import React from "react";
import {
  Typography,
  Box,
  Container,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useMediaQuery,
  useTheme,
  Stack
} from "@mui/material";
import {
  Person,
  Email,
  Cake,
  PhoneIphone,
  Group,
  Link as LinkIcon,
  Badge
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import CopiedTooltip from "../common/CopiedTooltip";
import { useLocation } from "react-router-dom";

const UserProfile = (props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down("sm"));

  const user = props?.user || location?.state?.user;
  const shareLink = props?.shareLink || location?.state?.shareLink;

  // Helper to render a clean info row
  const InfoRow = ({ icon, label, value, secondaryAction = null }) => (
    <>
      <ListItem 
        sx={{ px: 2, py: 0.5 }}
        secondaryAction={secondaryAction}
      >
        <ListItemIcon sx={{ color: "primary.main", minWidth: 40 }}>
          {React.cloneElement(icon, {
            sx: { color: "inherit" }
          })}
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography variant="caption" sx={{ opacity: 0.7, textTransform: 'uppercase', letterSpacing: 1 }} >
              {label}
            </Typography>
          }
          secondary={
            <Typography variant="body1" fontWeight="500" >
              {value || "—"}
            </Typography>
          }
        />
      </ListItem>
      <Divider component="li" sx={{ opacity: 0.1 }} />
    </>
  );

  return (
    <Container sx={{
      py: {
        xs: 10, sm: 0, md: 0, lg: 0
      },
      mb: 1,
    }}>
      {!mobileView && (
        <Typography 
          fontWeight="bold" 
          gutterBottom
          sx={{ mb: 3, color: process.env.REACT_APP_COLOR_TEXT || "white" }}
        >
          {t("walletcard.myaccount")}
        </Typography>
      )}

      <Paper 
        elevation={0} 
        sx={{ 
          borderRadius: 3, 
          overflow: "hidden",
          backgroundColor: process.env.REACT_APP_IS_LIGHT_THEME === 'true' 
          ? 'rgba(0, 0, 0, 0.05)'  // darken for light theme
          : 'rgba(255, 255, 255, 0.05)', // lighten for dark theme
          border: "1px solid rgba(255, 255, 255, 0.1)",
          backdropFilter: "blur(10px)",
          color: process.env.REACT_APP_IS_LIGHT_THEME === 'true' ? "black" : "white"
        }}
      >
        <List disablePadding>
          <InfoRow 
            icon={<Person />} 
            label={t("walletcard.myaccountpage.username")} 
            value={user?.username} 
          />
          <InfoRow 
            icon={<Badge />} 
            label={t("walletcard.myaccountpage.fullname")} 
            value={user?.fullname} 
          />
          <InfoRow 
            icon={<Cake />} 
            label={t("walletcard.myaccountpage.dob")} 
            value={user?.dateOfBirth} 
          />
          <InfoRow 
            icon={<Email />} 
            label={t("walletcard.myaccountpage.email")} 
            value={user?.email} 
          />
          <InfoRow 
            icon={<PhoneIphone />} 
            label={t("walletcard.myaccountpage.mobilenumber")} 
            value={user?.mobileNumber} 
          />
          <InfoRow 
            icon={<Group />} 
            label={t("walletcard.myaccountpage.affiliateCount")} 
            value={user?.affiliateCount} 
          />
          
          {/* Special Row for Referral Link */}
          <Box sx={{ p: 2, mt: 1, bgcolor: "rgba(0,0,0,0.2)" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.7, display: 'flex', alignItems: 'center', gap: 0.5}}>
                  <LinkIcon fontSize="inherit" /> {t("walletcard.myaccountpage.referrallink")}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mt: 0.5, 
                    color: "primary.light", 
                    wordBreak: "break-all",
                    fontFamily: "monospace" 
                  }}
                >
                  {shareLink}
                </Typography>
              </Box>
              <Box>
                <CopiedTooltip text={shareLink} />
              </Box>
            </Stack>
          </Box>
        </List>
      </Paper>
    </Container>
  );
};

export default UserProfile;